import React, { useEffect, useState } from "react";

import {
  List,
  DataTable,
  ColumnsButton,
  NumberField,
  TextField,
  TextInput,
  NumberInput,
  ReferenceInput,
  ArrayInput,
  SimpleForm,
  SimpleFormIterator,
  Create,
  Edit,
  minValue,
  number,
  Pagination,
  Show,
  AutocompleteInput,
  required,
  ReferenceField,
  TabbedShowLayout,
  Tab,
  EditButton,
  maxValue,
  useRecordContext,
  CreateButton,
  useListContext,
  Button,
  TopToolbar,
  FilterButton,
  ReferenceManyField,
  SelectInput,
  BooleanField,
  useNotify,
} from "react-admin";

import Typography from "@mui/material/Typography";

// Lib
import { custInfo } from "../lib/customer";
import { hasScope, SCOPE } from "../lib/auth";
import { DetailsButton } from "../lib/detailsButton";
import { ContextTagField } from "../lib/contextTagField";
import { VolumeField } from "../lib/volume";
import { SectionHeader } from "../lib/sectionHeader";
import { CreateVCButton } from "../lib/createVCButton";
import {
  MeasureOrAddDetailsButton,
  MeasureOrAddField,
} from "../lib/measureOrAddField";
import {
  CustomDateField,
  CustomDatetimeField,
  CustomDateTimeInput,
} from "../lib/datetime";
import { AddParameterIndicator } from "../lib/addParameterIndicator";
import { useDeviceHiddenColumns } from "../lib/useDeviceHiddenColumns";
import { ChatBubbleTooltip } from "../lib/chatBubbleTooltip";

import { AliasOrBarcodeReference } from "../lib/aliasWithBarcodeTooltip";
import { DownloadCSV } from "../lib/csvDownload";
import { ResetExpandedRows } from "../lib/resetExpandedRows";
import RangePickerInput from "../lib/dateRangePicker";
import { fetchJson } from "../lib/httpClient";
import { Sankey, SankeyNode, SankeyLink } from "../charts/sankey";

// MUI
import IconDownload from "@mui/icons-material//CloudDownload";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconCancel from "@mui/icons-material//Cancel";
import DialogActions from "@mui/material/DialogActions";
import { CustomTab } from "../lib/customTab";
import { LotVintraceTransactionsTab } from "./vintrace_transactions";
import { VirtualCompositeMeasurementsPanel } from "./virtual_composites";
import { LotAdditionAdditionsPanel } from "./lot_add";

const autoCompleteCondition = (val) => !!val && val.trim().length >= 2;

const archivedFilterChoices = [
  { id: false, name: "Hide archived" },
  { id: true, name: "Show only archived" },
];

const lotFilters = [
  <TextInput source="search" label="Lot" alwaysOn resettable />,
  <SelectInput
    source="archived"
    label="Archived"
    alwaysOn
    choices={archivedFilterChoices}
    emptyText="Show all"
  />,
  <TextInput source="intendedUse" label="Intended Use" resettable />,
];

const LotAppBarTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span style={{ marginRight: "0.5em" }}> {"Lot " + record.name}</span>;
};

const LotExportButton = () => {
  const [showDialog, setShowDialog] = useState(false);
  const { sort, filterValues } = useListContext();
  const notify = useNotify();

  const handleDownload = async () => {
    setShowDialog(false);

    try {
      await DownloadCSV(
        "BarrelWise_Lots",
        custInfo.customerID,
        "lots",
        sort,
        filterValues,
      );
    } catch (err) {
      console.log(err);
      notify(`Failed to download CSV: ${err}`, { type: "error" });
    }
  };

  return (
    <>
      <Button
        onClick={() => {
          setShowDialog(true);
        }}
        label="Export"
      >
        <IconDownload />
      </Button>
      <Dialog fullWidth open={showDialog}>
        <DialogTitle>CSV Export</DialogTitle>
        <DialogActions>
          <Button
            label="ra.action.cancel"
            variant="outlined"
            onClick={() => {
              setShowDialog(false);
            }}
          >
            <IconCancel />
          </Button>
          <Button label="Download" variant="contained" onClick={handleDownload}>
            <IconDownload />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const LotListActions = () => {
  return (
    <TopToolbar>
      <FilterButton />
      <CreateButton disabled={!hasScope(SCOPE.MANAGER)} />
      <LotExportButton />
      <ColumnsButton />
    </TopToolbar>
  );
};

const styles = {
  innovintName: {
    color: "#AAA",
    fontStyle: "italic",
    //fontSize: "90%",
  },
};

export const LotNameField = (_props?: { label?: string }) => {
  // Shows the InnoVint Lot name in addition to our lot name
  const record = useRecordContext();

  return (
    <Typography variant="body2">
      <TextField source="name" />
      {record.custom?.innovintLot?.name ? (
        <span style={styles.innovintName}>
          {" "}
          {record.custom?.innovintLot?.name}
        </span>
      ) : null}
    </Typography>
  );
};

// Columns hidden by default on phones; the rest stay visible. Users can
// re-enable any of them via the Columns button (their choice then wins
// over the device default for the session).
const LOT_LIST_PHONE_HIDDEN_COLUMNS = ["date", "intendedUse", "addTarget"];

// A <DataTable> instead of <Datagrid>: a plain table in a horizontal-scroll
// wrapper, so columns keep a usable width on phones instead of being
// compressed to nothing.
export const LotList = () => {
  const hiddenColumns = useDeviceHiddenColumns(LOT_LIST_PHONE_HIDDEN_COLUMNS);
  return (
    <List
      perPage={100}
      pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
      filters={lotFilters}
      filterDefaultValues={{ archived: false }}
      actions={<LotListActions />}
      sort={{ field: "id", order: "ASC" }}
      storeKey={false}
      empty={false}
    >
      <DataTable
        bulkActionButtons={false}
        rowClick={false}
        hiddenColumns={hiddenColumns}
        sx={{ "& .RaDataTable-tableWrapper": { overflowX: "auto" } }}
      >
        <DataTable.Col source="id" label="Lot">
          <ReferenceField source="id" reference="lots" link="show">
            <LotNameField />
          </ReferenceField>
        </DataTable.Col>
        <DataTable.Col source="date" label="Creation Date">
          <CustomDateField source="date" />
        </DataTable.Col>
        <DataTable.Col source="blendID" label="Blend">
          <ReferenceField source="blendID" reference="blends" link="show">
            <TextField source="name" />
          </ReferenceField>
        </DataTable.Col>
        <DataTable.Col source="intendedUse" label="Intended Use">
          <TextField source="intendedUse" />
        </DataTable.Col>
        <DataTable.Col source="addTarget" label={"Target Free SO\u2082 (ppm)"}>
          <NumberField source="addTarget" />
        </DataTable.Col>
        <DataTable.Col source="barrelCount" label="Vessels">
          <NumberField source="barrelCount" />
        </DataTable.Col>
        <DataTable.Col source="capacity" label="Capacity">
          <VolumeField source="capacity" />
        </DataTable.Col>
        <DataTable.Col label="Addition" disableSort>
          <AddParameterIndicator />
        </DataTable.Col>
        {hasScope(SCOPE.ADMIN) && (
          <DataTable.Col>
            <CreateVCButton />
          </DataTable.Col>
        )}
        <DataTable.Col>
          <DetailsButton />
        </DataTable.Col>
        {hasScope(SCOPE.MANAGER) && (
          <DataTable.Col>
            <EditButton />
          </DataTable.Col>
        )}
      </DataTable>
    </List>
  );
};

const validateTarget = [number(), minValue(0), maxValue(100)];
const validateMaxAdd = [number(), minValue(0), maxValue(100)];
const validateMinAdd = [number(), minValue(0), maxValue(100)];
// FIXME: the multiplication store the value in float point (e.g. 1.2) right now
// does RA wants to use this format or should it be in percentage e.g. 120%
const validateMultiplier = [number(), minValue(0), maxValue(10)];
const BarrelAssociationInput = () => {
  const getOptionText = (option) => {
    return option?.alias !== undefined && option?.alias !== ""
      ? `${option?.alias} (Barcode: ${option?.barcode})`
      : `${option?.barcode}`;
  };

  return (
    <ArrayInput source="barrelAssociations" label="Vessel Association">
      <SimpleFormIterator>
        <ReferenceInput source="barrelID" reference="vessels" perPage={100}>
          <AutocompleteInput
            optionText={getOptionText}
            shouldRenderSuggestions={autoCompleteCondition}
            filterToQuery={(searchText) =>
              autoCompleteCondition(searchText)
                ? { search: searchText, simple: true }
                : { search: "", simple: true }
            }
            label="Vessel"
          />
        </ReferenceInput>
      </SimpleFormIterator>
    </ArrayInput>
  );
};

const CreateEditForm = () => (
  <SimpleForm>
    <CustomDateTimeInput label="date" source="date" defaultValue={new Date()} />
    <TextInput label="Lot" source="name" validate={required()} />
    <ReferenceInput
      source="blendID"
      reference="blends"
      sort={{ field: "name", order: "ASC" }}
    >
      <AutocompleteInput
        optionText="name"
        filterToQuery={(searchText) => ({ name: searchText })}
        label="Blend"
        validate={required()}
      />
    </ReferenceInput>
    <TextInput label="Intended Use" source="intendedUse" />
    <SectionHeader title="Additions Recommendations" />
    <NumberInput
      source="addTarget"
      label={"Target Free SO\u2082 (ppm)"}
      validate={validateTarget}
    />
    <NumberInput
      source="addMin"
      label={"Min. Free SO\u2082 Addition (ppm)"}
      validate={validateMinAdd}
    />
    <NumberInput
      source="addMax"
      label={"Max. Free SO\u2082 Addition (ppm)"}
      validate={validateMaxAdd}
    />
    <NumberInput
      source="addMultiplier"
      label={"Free SO\u2082 Multiplier"}
      validate={validateMultiplier}
    />
    {hasScope(SCOPE.ADMIN) ? <BarrelAssociationInput /> : null}
  </SimpleForm>
);

export const LotEdit = () => (
  <Edit title={<LotAppBarTitle />} redirect="list">
    <CreateEditForm />
  </Edit>
);

export const LotCreate = () => (
  <Create redirect="list">
    <CreateEditForm />
  </Create>
);

const LotMeasurementList = () => (
  <>
    <DataTable bulkActionButtons={false} rowClick={false}>
      <DataTable.Col source="barrelID" label="Vessel" disableSort>
        <AliasOrBarcodeReference
          source="barrelID"
          reference="vessels"
          sortable={false}
        />
      </DataTable.Col>
      <DataTable.Col source="fso2" label="Free SO₂ (ppm)" disableSort>
        <NumberField source="fso2" label={"Free SO\u2082 (ppm)"} />
      </DataTable.Col>
      <DataTable.Col source="datetime" label="Measurement Time" disableSort>
        <CustomDatetimeField source="datetime" label="Measurement Time" />
      </DataTable.Col>
      <DataTable.Col>
        <DetailsButton />
      </DataTable.Col>
    </DataTable>
    <Pagination rowsPerPageOptions={[]} />
  </>
);

const LotBarrelsList = () => (
  <>
    <DataTable bulkActionButtons={false} rowClick={false}>
      <DataTable.Col source="barrelID" label="Vessel" disableSort>
        <AliasOrBarcodeReference
          source="barrelID"
          reference="vessels"
          sortable={false}
        />
      </DataTable.Col>
      <DataTable.Col source="capacity" label="Capacity" disableSort>
        <ReferenceField
          source="barrelID"
          reference="vessels"
          label="Capacity"
          link={false}
          sortable={false}
        >
          <VolumeField source="capacity" label="Capacity" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="startTime" label="Date Added" disableSort>
        <CustomDatetimeField source="startTime" label="Date Added" />
      </DataTable.Col>
      <DataTable.Col source="endTime" label="Date Removed" disableSort>
        <CustomDatetimeField source="endTime" label="Date Removed" />
      </DataTable.Col>
      <DataTable.Col source="lastFSO2Read" label="Free SO₂ (ppm)" disableSort>
        <ReferenceField
          source="barrelID"
          reference="vessels"
          label={"Free SO\u2082 (ppm)"}
          link={false}
          sortable={false}
        >
          <NumberField source="lastFSO2Read.fso2" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="lastFSO2Read" label="Last Analyzed" disableSort>
        <ReferenceField
          source="barrelID"
          reference="vessels"
          label="Last Analyzed"
          link={false}
          sortable={false}
        >
          <CustomDatetimeField source="lastFSO2Read.datetime" />
        </ReferenceField>
      </DataTable.Col>
      {hasScope(SCOPE.ADMIN) && (
        <DataTable.Col>
          <EditButton />
        </DataTable.Col>
      )}
    </DataTable>
    <Pagination rowsPerPageOptions={[]} />
  </>
);

const LotEventListFilters = [
  <SelectInput
    label="Type"
    source="type"
    choices={[
      { id: "lot composite", name: "Lot Composite" },
      { id: "virtual composite", name: "Virtual Composite" },
      { id: "any composite", name: "Any Composite" },
      { id: "lot addition", name: "Lot Addition" },
    ]}
    emptyText={"All"}
  />,
  <RangePickerInput source="dateRange" label="Date" />,
];

// Event rows in the FSO2 Events tab reference the underlying records by
// eventID, so these remap the record context before delegating to the
// shared panels.
const LotEventAdditionsPanel = () => {
  const record = useRecordContext();
  if (!record || record.type !== "lot addition") return null;

  return <LotAdditionAdditionsPanel record={{ id: record.eventID }} />;
};

const LotEventVCMeasurementsPanel = () => {
  const record = useRecordContext();
  if (!record || record.type !== "virtual composite") return null;

  return (
    <ReferenceField reference="virtual_composite" source="eventID" link={false}>
      <VirtualCompositeMeasurementsPanel />
    </ReferenceField>
  );
};

const LotFSO2EventList = () => {
  const record = useRecordContext();
  return (
    <List
      perPage={100}
      pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
      filter={{ lotID: record.id }}
      filters={LotEventListFilters}
      title={" "}
      storeKey={false}
      empty={false}
    >
      <ResetExpandedRows />
      <DataTable
        bulkActionButtons={false}
        rowClick={false}
        expand={
          <>
            <LotEventAdditionsPanel />
            <LotEventVCMeasurementsPanel />
          </>
        }
        isRowExpandable={(record) =>
          record.type === "lot addition" || record.type === "virtual composite"
        }
      >
        <DataTable.Col source="type" label="Event" disableSort>
          <TextField source="type" label="Event" />
        </DataTable.Col>
        <DataTable.Col source="fso2" label="Free SO₂ (ppm)" disableSort>
          <MeasureOrAddField label="Free SO₂ (ppm)" />
        </DataTable.Col>
        <DataTable.Col source="type" label="Context" disableSort>
          <ContextTagField source="type" />
        </DataTable.Col>
        <DataTable.Col source="datetime" label="Time" disableSort>
          <CustomDatetimeField source="datetime" label="Time" />
        </DataTable.Col>
        <DataTable.Col source="notes" label="Notes" disableSort>
          <ChatBubbleTooltip source="notes" />
        </DataTable.Col>
        <DataTable.Col disableSort>
          <MeasureOrAddDetailsButton />
        </DataTable.Col>
      </DataTable>
    </List>
  );
};

// The shape of the vintrace batch movement graph returned by the
// vintrace_transactions/graph endpoint.
interface VintraceGraphData {
  batch: string;
  unit: string;
  depth: number;
  nodes: SankeyNode[];
  links: SankeyLink[];
  totals: {
    movements: number;
    volume: number;
    minDate?: string;
    maxDate?: string;
  };
}

const fmtVintraceDate = (s?: string) => (s ? s.slice(0, 10) : "-");

// The TabbedShowLayout instantiates each tab component twice (once for the
// tab bar header and once for the content), and React's StrictMode
// double-runs effects in dev, so a bare fetch in the effect below would
// fire several identical requests per tab open. Concurrent callers share
// one in-flight request; a later remount (re-opening the tab) refetches.
const inFlightGraphFetches = new Map<string, Promise<VintraceGraphData>>();

const fetchVintraceGraph = (customerID: number, lotID: number | string) => {
  const key = `${customerID}:${lotID}`;
  const pending = inFlightGraphFetches.get(key);
  if (pending) return pending;
  const p = fetchJson(
    `/api/v2/c/${customerID}/vintrace_transactions/graph?lotID=${lotID}&depth=4`,
  )
    .then((res) => res.json as VintraceGraphData)
    .finally(() => {
      inFlightGraphFetches.delete(key);
    });
  inFlightGraphFetches.set(key, p);
  return p;
};

// LotVintraceGraphTab shows the vintrace batch movement graph of the lot's
// vintrace batch (lot.custom.vintraceBatch) as a sankey diagram; the POC
// behind it is hacks/vintrace_graph. The tab follows the CustomTab pattern
// of LotVintraceTransactionsTab: props are spread onto the Tab so the
// TabbedShowLayout can render it in the header (label only) and in the
// content (tab body). Admin only.
export const LotVintraceGraphTab = (props: any) => {
  const record = useRecordContext();
  const [graph, setGraph] = useState<VintraceGraphData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // The header instance only renders the tab label (the TabbedShowLayout
    // clones the tab with props.context === 'header'); it must not fetch.
    if (props.context === "header") return;
    if (!record?.id || !record?.customerID) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setGraph(null);
    fetchVintraceGraph(record.customerID, record.id)
      .then((g) => {
        if (!cancelled) setGraph(g);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err?.message ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Depend on record.id (not record, whose identity changes on every
    // render) so the fetch doesn't retrigger on unrelated re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id, record?.customerID, props.context]);

  if (!record) return null;

  let body: React.ReactElement;
  if (loading) {
    body = <Typography variant="body2">Loading…</Typography>;
  } else if (error) {
    body = (
      <Typography variant="body2" color="text.secondary">
        {error}
      </Typography>
    );
  } else if (!graph || graph.nodes.length === 0) {
    body = (
      <Typography variant="body2" color="text.secondary">
        No movements found for this lot's vintrace batch.
      </Typography>
    );
  } else {
    body = (
      <Sankey
        title={`Batch movements: ${graph.batch}`}
        subtitle={`${custInfo?.customerLongName ?? ""} · depth ${graph.depth} · forward and backward from the start batch · ${graph.nodes.length} nodes, ${graph.totals.movements} movements · movements only (analyses and additions excluded)`}
        footer={`${graph.totals.movements} movements · ${Math.round(
          graph.totals.volume,
        )} ${graph.unit} total · ${fmtVintraceDate(graph.totals.minDate)} .. ${fmtVintraceDate(graph.totals.maxDate)}`}
        unit={graph.unit}
        nodes={graph.nodes}
        links={graph.links}
        exportName={`vintrace-graph-${record.name || record.id}`}
      />
    );
  }

  return (
    <Tab label="Vintrace Graph" {...props}>
      {body}
    </Tab>
  );
};

export const LotShow = () => (
  <Show title={<LotAppBarTitle />}>
    <TabbedShowLayout>
      <Tab label="Overview">
        <CustomDateField source="date" label="Creation Date" />
        <LotNameField label="Lot" />
        <ReferenceField
          source="blendID"
          reference="blends"
          label="Blend"
          link="show"
        >
          <TextField source="name" />
        </ReferenceField>
        <NumberField
          source="addTarget"
          label={"Target Free SO\u2082 (ppm)"}
          emptyText={"\u00A0"}
        />
        <NumberField
          source="addMin"
          label={"Min. Free SO\u2082 Addition (ppm)"}
          emptyText={"\u00A0"}
        />
        <NumberField
          source="addMax"
          label={"Max. Free SO\u2082 Addition (ppm)"}
          emptyText={"\u00A0"}
        />
        <NumberField
          source="addMultiplier"
          label={"Free SO\u2082 Multiplier"}
          emptyText={"\u00A0"}
        />
        <TextField
          source="intendedUse"
          label="Intended Use"
          emptyText={"\u00A0"}
        />
        <NumberField source="barrelCount" label="Vessels" />
        <VolumeField source="capacity" label="Capacity" />
        <BooleanField source="archived" label="Archived" />
      </Tab>
      <Tab label="Vessels">
        <ReferenceManyField reference="lot_barrel" target="lotID" label="">
          <LotBarrelsList />
        </ReferenceManyField>
      </Tab>
      <Tab label="FSO2 Events">
        <ReferenceManyField reference="events" target="lotID" label="">
          <LotFSO2EventList />
        </ReferenceManyField>
      </Tab>
      <Tab label="Measurements">
        <ReferenceManyField
          reference="events/fso2_reading"
          target="lotID"
          label=""
        >
          <LotMeasurementList />
        </ReferenceManyField>
      </Tab>
      <Tab label="Analyses">
        <ReferenceManyField
          reference="analyses"
          target="lotID"
          sort={{ field: "timestamp", order: "DESC" }}
          label=""
        >
          <DataTable bulkActionButtons={false} rowClick={false}>
            <DataTable.Col source="timestamp" label="Timestamp">
              <CustomDatetimeField source="timestamp" label="Timestamp" />
            </DataTable.Col>
            <DataTable.Col source="analyte" label="Analyte" disableSort>
              <ReferenceField
                source="analysisTypeID"
                reference="analysis_types"
                sortable={false}
              >
                <TextField source="analyte" />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col source="value" label="Value" disableSort>
              <NumberField source="value" label="Value" />
            </DataTable.Col>
            <DataTable.Col source="unit" label="Unit" disableSort>
              <ReferenceField
                source="analysisTypeID"
                reference="analysis_types"
                sortable={false}
              >
                <TextField source="unit" />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col source="method" label="Method" disableSort>
              <TextField source="method" label="Method" />
            </DataTable.Col>
            <DataTable.Col source="lotID" label="Lot" disableSort>
              <ReferenceField source="lotID" reference="lots" sortable={false}>
                <TextField source="name" />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col source="vesselID" label="Vessel" disableSort>
              <AliasOrBarcodeReference
                source="vesselID"
                reference="vessels"
                label="Vessel"
                sortable={false}
              />
            </DataTable.Col>
            <DataTable.Col>
              <DetailsButton />
            </DataTable.Col>
          </DataTable>
        </ReferenceManyField>
      </Tab>
      <CustomTab />
      {hasScope(SCOPE.ADMIN) ? <LotVintraceTransactionsTab /> : null}
      {hasScope(SCOPE.ADMIN) ? <LotVintraceGraphTab /> : null}
    </TabbedShowLayout>
  </Show>
);
